import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { faceKey, OPPOSITE, type Face } from './faces';
import { modalBackdropCls, modalCardCls, modalCloseCls, zoomOverlayCls } from './overlay';
import type { OrientAnswer, OrientView } from './threeDApi';
import { useLanguage } from '../../contexts/LanguageContext';

// WHAT THE ORIENTATION CALL SAID ABOUT EACH OF THE SIX VIEWS, AND WHICH OF IT DOES NOT ADD UP.
//
// The call used to be asked for TWO views at right angles, which is a rotation by
// construction — there was nothing for an answer to contradict. It is asked about all six
// independently now (see mesh_orient), and six independent answers can disagree: two views
// claiming one side of the product, or a view and its opposite claiming two sides that are
// not opposite. The turn applied is the one that explains the most of them, and the ones it
// does not explain are exactly what this draws in red.
//
// WHY THEY ARE SHOWN RATHER THAN HIDDEN. A disagreement here is the only signal this app has
// that a turn might be wrong. The mesh swings either way, the part looks plausible either
// way, and a quiet best-fit would mean the one case worth checking is the one that looks
// identical to the twenty that were right.
//
// Its own file because the panel it sits in is a hover away from every other control on a
// mesh, and MeshControls is long enough that a nested modal and its explanations would be
// read as part of something else.

// The letters under the six renders, in the order they are sent. `mesh_orient.VIEW_TOKENS`
// on the other side of the wire.
const LETTERS = 'ABCDEF';

// The other end of the same axis. The six go up as three opposed pairs — A/B, C/D, E/F — so
// the twin of a letter is the one beside it, which is what `^ 1` says.
//
// NOTHING HERE KNOWS WHICH SIDE OF THE MESH A LETTER IS, and that is deliberate on both sides
// of the wire: the model is shown letters precisely so it cannot be nudged by the file's own
// labelling, and a panel that named them would put the nudge back for the person reading it.
// The pairing is all this needs, and it is a fact about how the pictures were made.
const twinOf = (view: string): string => {
  const at = LETTERS.indexOf((view || '').toUpperCase());
  return at < 0 ? '' : LETTERS[at ^ 1];
};

const isFace = (s: string | undefined): s is Face =>
  !!s && s in OPPOSITE;

// The mesh face each lettered view looks at, before any turn: A is the render taken down the
// file's own front, B its back, and so on. `mesh_orient.VIEW_ORDER`, which is the mapping the
// model is never shown.
const VIEW_ORDER: Face[] = ['front', 'back', 'left', 'right', 'top', 'bottom'];

// WHAT THE MODEL SAID ABOUT EACH FACE OF THE PART AS IT STANDS NOW, keyed by the face's own
// name — which is what the viewer writes on its bounding box, and therefore the only key that
// can put the answer on the right plate.
//
// TWO MAPPINGS, and which one applies is whether a turn happened.
//
//   A TURN WAS APPLIED, so the mesh was re-authored: the face the box now calls the `top` is
//   whichever old face the turn carried there, and `applied` is exactly that — the product
//   side each view's face landed on. So the view whose `applied` is this face is the view of
//   this face, and what it `shows` is what was said about it.
//
//   NOTHING WAS TURNED, so the mesh is in the frame the answer was given in and the letters
//   still point at the file's own faces: A is the front, B the back, in VIEW_ORDER.
//
// Both are only true while the mesh has not moved since — see orientAnswers, which is where
// that is kept true.
export type FaceSaid = { view: string; shows: string; fits: boolean };

export function faceSaidOf(answer: OrientAnswer | null): Record<string, FaceSaid> {
  const out: Record<string, FaceSaid> = {};
  for (const v of answer?.views ?? []) {
    const at = LETTERS.indexOf((v.view || '').toUpperCase());
    const face = v.applied ?? (at >= 0 ? VIEW_ORDER[at] : undefined);
    if (face) out[face] = { view: v.view, shows: v.shows, fits: v.fits !== false };
  }
  return out;
}

// WHY ONE ANSWER DOES NOT FIT, in as many sentences as there are reasons — computed here from
// the six answers rather than asked of the model.
//
// It is arithmetic, and the model is the thing being checked: asking it to explain its own
// contradiction is asking the same reader that produced it, and it would answer plausibly
// whatever the truth was. Two of the three sentences are facts about the answer set ("D says
// the front as well"), and the third is what the applied turn makes of this view.
function whyItDoesNotFit(
  v: OrientView, views: OrientView[], t: (k: string) => string,
): string[] {
  const said = (f: string) => (isFace(f) ? t(faceKey(f)).toLowerCase() : f);
  const out: string[] = [];

  // TWO VIEWS ON ONE SIDE. Six pictures show six different sides of a product, so a side
  // claimed twice is one claim too many whichever of them is right.
  const also = views.filter((o) => o !== v && o.shows === v.shows).map((o) => o.view);
  if (also.length) {
    out.push(t('notice.3d.orientWhyDuplicate')
      .replace('{others}', also.join(', '))
      .replace('{side}', said(v.shows)));
  }

  // A VIEW AND ITS OPPOSITE. The two look along opposite directions, so whatever they show
  // has to be opposite too — this is the check that catches a top and a bottom answered onto
  // two sides that are next to each other.
  const twin = views.find((o) => o.view === twinOf(v.view));
  if (twin && isFace(v.shows) && twin.shows !== OPPOSITE[v.shows]) {
    out.push(t('notice.3d.orientWhyOpposite')
      .replace('{v}', v.view)
      .replace('{twin}', twin.view)
      .replace('{twinSaid}', said(twin.shows))
      .replace('{opposite}', said(OPPOSITE[v.shows])));
  }

  // AND WHAT THE APPLIED TURN MAKES OF IT — last, because it is the consequence rather than
  // the reason. Absent where nothing was applied, which is its own sentence above the list.
  if (v.applied) {
    out.push(t('notice.3d.orientWhyApplied')
      .replace('{said}', said(v.shows))
      .replace('{applied}', said(v.applied)));
  }
  return out;
}

// The explanation, over the panel it was opened from.
//
// ESCAPE IS CAUGHT ON THE WINDOW, IN THE CAPTURE PHASE, and stopped there. The panel behind
// this is itself a modal listening on the document (see CallBadge) — and the capture phase
// reaches the window before the document, so this is the one place a key can be taken before
// the thing underneath sees it. Without that, one press closes the explanation, the answer
// panel and the badge together.
export function Why({ view, views, settled, turns, onClose }: {
  view: OrientView;
  views: OrientView[];
  settled: boolean;
  turns: number;
  onClose: () => void;
}) {
  const { t } = useLanguage();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const fits = settled && view.fits !== false;
  const lines = !settled
    ? [t('notice.3d.orientWhyUnsettled').replace('{n}', String(turns || 0))]
    // A VIEW THAT AGREES IS WORTH OPENING TOO, now that the way in is a label on the part
    // rather than a red chip: somebody pressing the word on a face is asking what the model
    // said about it, and "nothing is wrong with this one" is an answer to that. It would
    // otherwise fall through to the list below and print the consequence of a disagreement
    // that is not there.
    : fits
      ? [t('notice.3d.orientWhyFits').replace(
        '{said}',
        isFace(view.shows) ? t(faceKey(view.shows)).toLowerCase() : view.shows,
      )]
      : whyItDoesNotFit(view, views, t);

  return createPortal(
    // Above the panel that opened it — zoomOverlayCls is the z the picture zoom uses over
    // this same modal, and for the same reason.
    //
    // THE CARD EVERY DIALOG WEARS (modalCardCls), white or slate-900 with the theme — it was
    // slate-900 in both. Its border goes rose when a view does not fit: the whole card is
    // the disagreement, and the edge says so before the words do. `!` because modalCardCls
    // carries its own border colours and two utilities of one property are settled by the
    // stylesheet's order, not by which is written last.
    <div className={`${zoomOverlayCls} ${modalBackdropCls}`} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-md p-5 pr-10 text-left ${modalCardCls} ${
          fits ? '' : 'border-rose-300!'
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('notice.3d.turnClose')}
          className={modalCloseCls}
        >
          <X className="h-5 w-5" />
        </button>
        <p className={`mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide ${
          fits ? 'text-slate-500' : 'text-rose-600'
        }`}
        >
          {!fits && <AlertTriangle className="h-4 w-4 shrink-0" />}
          {t(fits ? 'notice.3d.orientFitsTitle' : 'notice.3d.orientWhyTitle')
            .replace('{v}', view.view)}
        </p>
        <ul className="space-y-2 text-xs leading-relaxed text-slate-700">
          {lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  );
}

// THE SAME EXPLANATION, ASKED FOR BY FACE rather than by view — which is how the viewer asks
// it: what was pressed there is a word written on one side of the part's box, and the box
// knows its faces by name and nothing about letters.
//
// Renders nothing where that face has no answer, which is every face of a part nobody has
// asked about and the missing letters of an answer that came back short.
export function OrientFaceWhy({ answer, face, onClose }: {
  answer: OrientAnswer | null;
  face: string;
  onClose: () => void;
}) {
  const said = faceSaidOf(answer)[face];
  const view = answer?.views?.find((v) => v.view === said?.view);
  if (!answer || !view) return null;
  return (
    <Why
      view={view}
      views={answer.views ?? []}
      settled={answer.settled !== false}
      turns={answer.turns ?? 0}
      onClose={onClose}
    />
  );
}

// The answer as a row of chips, one per view, the ones that do not fit outlined in red and
// pressable.
export function OrientViews({ answer }: { answer: OrientAnswer }) {
  const { t } = useLanguage();
  const [why, setWhy] = useState<OrientView | null>(null);
  const views = answer.views ?? [];
  if (!views.length) return null;

  // A TURN WAS NAMED. Absent on every answer recorded before this field existed — which were
  // all settled by construction, being two perpendicular faces — so a missing value reads as
  // yes rather than as an alarm on the whole history.
  const settled = answer.settled !== false;
  // And which chips are wrong: `false` and not "not true", for the same reason. A stored
  // two-view answer has no `fits` on it and nothing to draw red.
  const flagged = (v: OrientView) => v.fits === false || !settled;

  return (
    <div>
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">
        {t('notice.3d.orientAnswerTitle')}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {views.map((v) => {
          const line = t('notice.3d.orientAnswerLine')
            .replace('{v}', v.view)
            .replace('{s}', isFace(v.shows) ? t(faceKey(v.shows)).toLowerCase() : v.shows);
          if (!flagged(v)) {
            return (
              <span
                key={v.view}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700"
              >
                {line}
              </span>
            );
          }
          return (
            <button
              key={v.view}
              type="button"
              onClick={() => setWhy(v)}
              title={t('notice.3d.orientWhyOpen')}
              className="rounded-md border border-rose-500 px-2 py-1 text-xs text-rose-300 transition-colors hover:bg-rose-500/10"
            >
              {line}
            </button>
          );
        })}
      </div>
      {/* WHAT THE RED MEANS, once, under the row — rather than inside every explanation.
          Two states, and they are different failures: a turn was applied and some of the
          answers disagree with it, or the answers named no single turn and nothing was
          applied at all. */}
      {views.some(flagged) && (
        <p className="mt-2 text-[11px] leading-snug text-rose-300">
          {settled ? t('notice.3d.orientMisfitHint') : t('notice.3d.orientUnsettledHint')}
        </p>
      )}
      {/* THE ROW IS SHORT ON PURPOSE NOW, so nothing says so. It used to name the letters
          missing from a set of six — an entry the parser dropped, which was worth a line when
          six were asked for. Two are asked for now (see mesh_orient), so four letters are
          absent from every answer and a line listing them would be on screen always, saying
          nothing. An answer too short to fix a turn is refused before it gets here. */}
      {why && (
        <Why
          view={why}
          views={views}
          settled={settled}
          turns={answer.turns ?? 0}
          onClose={() => setWhy(null)}
        />
      )}
    </div>
  );
}
